import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useProductsTFunction } from '@retail/products/i18n';
import { MgSupplierItemPackagesTableRow } from './MgSupplierItemPackagesTableRow';
import Tooltip from '@mui/material/Tooltip';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Stack, styled } from '@mui/material';
import { SupplierItemPackage } from './SupplierItems';
import { MgSupplierItemCard } from '@retail/products/types';
import {
  getClassCodeLabel,
  getPackageTypeLabel,
  sortSupplierItemPackageType,
} from '@retail/products/utils';
import { Fragment } from 'react';

interface Props {
  packages: SupplierItemPackage[];
  selectedSupplier: Partial<MgSupplierItemCard> | null;
}

export function MgSupplierItemPackagesTable({ packages, selectedSupplier }: Props) {
  const t = useProductsTFunction();

  // Helper const to separate different packaging types with a packaging title/row
  let lastPackageType: SupplierItemPackage['type'];

  const basePackage = packages.find(({ basePriceUnit }) => basePriceUnit);
  const basePackageUnit = basePackage?.unit;

  return (
    <Box>
      <TableContainer sx={{ '& .MuiTable-root': { border: 0 } }}>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell>{t('products.mgItem.supplierItem.packageType.label')}</TableCell>
              <TableCell>{t('products.supplier.name')}</TableCell>
              <TableCell>{t('products.mgItem.supplierItem.gtin')}</TableCell>
              <TableCell>{t('products.mgItem.supplierItem.supplierUnit')}</TableCell>
              <TableCell>
                <Stack direction="row" gap="4px" alignItems="start">
                  {t('products.mgItem.supplierItem.mgUnit.label')}
                  <Tooltip arrow title={t('products.mgItem.supplierItem.mgUnit.description')}>
                    <InfoOutlined fontSize="small" />
                  </Tooltip>
                </Stack>
              </TableCell>

              <TableCell>{t('products.mgItem.supplierItem.consistsOf')}</TableCell>
              <TableCell
                sx={{
                  minWidth: 100,
                }}
              >
                {t('products.mgItem.supplierItem.grossWeight')}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              '& td p': {
                fontSize: 'inherit',
                lineHeight: 'inherit',
              },
            }}
          >
            {packages.sort(sortSupplierItemPackageType).map((supplierItemPackage, index) => {
              const isNewPackageType = lastPackageType !== supplierItemPackage.type;
              lastPackageType = supplierItemPackage.type;

              return (
                <Fragment
                  key={`${supplierItemPackage.mgSupplierId}_${index}_${supplierItemPackage?.calculatedCount}`}
                >
                  {isNewPackageType && (
                    <TableRow sx={{ backgroundColor: 'grey.A100' }}>
                      <Tooltip title={getPackageTypeLabel(t, supplierItemPackage.type ?? '')}>
                        <TableCell sx={{ fontWeight: '600 !important' }}>
                          {getClassCodeLabel(supplierItemPackage.type ?? '')}
                        </TableCell>
                      </Tooltip>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  )}

                  <MgSupplierItemPackagesTableRow
                    index={index}
                    {...supplierItemPackage}
                    basePackageUnit={basePackageUnit}
                    selectedSupplier={selectedSupplier}
                  />
                </Fragment>
              );
            })}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Box>
  );
}

const StyledTable = styled(Table)(({ theme }) => ({
  border: `1px solid ${theme.vars.palette.grey[50]}`,
  borderRadius: Number(theme.shape.borderRadius) * 2,

  '& .MuiTableHead-root': {
    backgroundColor: theme.vars.palette.success[50],

    '& .MuiTableCell-root': { ...theme.typography.body2, fontWeight: 'bold' },
  },
}));
